/** Only authored sparse water cells are rendered; intact land has no water mesh. */
import type { MapRect } from "../api/types";

/** Four subcells per original world unit, without changing the terrain projection. */
export const WATER_CELL_SIZE = 0.25;
// Do not impose an arbitrary water/river area quota. The editor's world
// boundary still keeps coordinates valid, while adjacent cells are rendered
// as merged rectangles rather than one Pixi graphics object per cell.
export const waterCellKey = (x: number, y: number): string =>
  `${Math.round(x / WATER_CELL_SIZE)},${Math.round(y / WATER_CELL_SIZE)}`;

/** Read the exact world coordinates back from integer quarter-grid keys. */
export function waterCellFromKey(key: string): [number, number] {
  const [x, y] = key.split(",").map(Number);
  return [x * WATER_CELL_SIZE, y * WATER_CELL_SIZE];
}

/** Existing maps omitted cellSize and stored one-by-one full-world-unit cells. */
export function expandLegacyWaterCells(cells: Array<[number, number]>): Array<[number, number]> {
  const expanded: Array<[number, number]> = [];
  for (const [x, y] of cells) {
    for (let iy = 0; iy < 4; iy++) for (let ix = 0; ix < 4; ix++) {
      expanded.push([x + ix * WATER_CELL_SIZE, y + iy * WATER_CELL_SIZE]);
    }
  }
  return expanded;
}

/** Coalesce adjacent grid cells into rectangular fills and cuts. */
export function mergeWaterCells(cells: Array<[number, number]>, cellSize = 1): MapRect[] {
  const rows = new Map<number, Set<number>>();
  for (const [x, y] of cells) {
    const gx = Math.round(x / cellSize), gy = Math.round(y / cellSize);
    if (!Number.isFinite(x) || !Number.isFinite(y) ||
        Math.abs(x - gx * cellSize) > 0.00001 ||
        Math.abs(y - gy * cellSize) > 0.00001) continue;
    let row = rows.get(gy);
    if (!row) { row = new Set(); rows.set(gy, row); }
    row.add(gx);
  }
  const merged: MapRect[] = [];
  let previous = new Map<string, MapRect>();
  let lastY: number | null = null;
  for (const y of Array.from(rows.keys()).sort((a, b) => a - b)) {
    // Missing rows must not connect unrelated rectangles.
    if (lastY !== null && y !== lastY + 1) previous = new Map();
    lastY = y;
    const xs = Array.from(rows.get(y)!).sort((a, b) => a - b);
    const next = new Map<string, MapRect>();
    let start: number | null = null;
    let last = 0;
    const append = (x0: number, x1: number) => {
      const key = `${x0}:${x1}`;
      const prior = previous.get(key);
      if (prior && Math.abs(prior.y + prior.height - y * cellSize) < 0.00001) {
        prior.height += cellSize;
        next.set(key, prior);
      } else {
        const rect: MapRect = {
          id: `water-${x0}-${y}`, x: x0 * cellSize, y: y * cellSize,
          width: (x1 - x0 + 1) * cellSize, height: cellSize,
        };
        merged.push(rect);
        next.set(key, rect);
      }
    };
    for (const x of xs) {
      if (start === null) start = x;
      else if (x !== last + 1) { append(start, last); start = x; }
      last = x;
    }
    if (start !== null) append(start, last);
    previous = next;
  }
  return merged;
}

/** Smooth, seamless low-contrast water. The highlight pass has a transparent background. */
export function createWaterTexture(hex: string, highlights = false): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  const size = 256;
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) return canvas;
  const value = /^#[0-9a-f]{6}$/i.test(hex) ? hex : "#137c96";
  const rgb = [1, 3, 5].map(i => Number.parseInt(value.slice(i, i + 2), 16));
  const pixels = ctx.createImageData(size, size);
  let seed = 63127;
  for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
    const u = x / size * Math.PI * 2, v = y / size * Math.PI * 2;
    // All integer frequencies on the torus: opposite edges join without seams.
    const broad = 0.52 * Math.sin(2 * v + 0.8 * Math.sin(u))
      + 0.27 * Math.sin(3 * u - v + Math.sin(2 * v))
      + 0.21 * Math.cos(u + 3 * v);
    const tiny = Math.sin(7 * v + 2 * Math.sin(2 * u)) *
      Math.cos(4 * u - v) * 0.2;
    seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
    const grain = (seed / 4294967296 - 0.5) * 2;
    const offset = (y * size + x) * 4;
    if (highlights) {
      // Translucent, uneven ribbons drift over the static deep-water surface.
      const ribbon = Math.max(0, broad + tiny * 0.8 + 0.08);
      const crest = Math.pow(Math.max(0, Math.sin(7 * v + 2 * Math.sin(2 * u))), 10);
      const alpha = Math.min(145, Math.round(Math.pow(ribbon, 1.5) * 130 + crest * 28));
      pixels.data[offset] = 183;
      pixels.data[offset + 1] = 225;
      pixels.data[offset + 2] = 231;
      pixels.data[offset + 3] = alpha;
    } else {
      const shade = broad * 7 + tiny * 3 + grain;
      pixels.data[offset] = Math.max(0, Math.min(255, rgb[0] + shade * 0.7));
      pixels.data[offset + 1] = Math.max(0, Math.min(255, rgb[1] + shade));
      pixels.data[offset + 2] = Math.max(0, Math.min(255, rgb[2] + shade * 1.1));
      pixels.data[offset + 3] = 255;
    }
  }
  ctx.putImageData(pixels, 0, 0);
  return canvas;
}
